import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { planAPI, authAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  Sparkles, Check, Zap, Crown, ArrowRight, CreditCard, Loader2, Info, Video, Hash, Tag, MessageSquare, FileText
} from 'lucide-react';
import { toast } from 'sonner';

const PLAN_META = {
  free: { icon: Zap, color: 'text-zinc-400', border: 'border-white/10', badge: 'bg-zinc-800 text-zinc-400', gradient: '' },
  starter: { icon: Sparkles, color: 'text-indigo-400', border: 'border-indigo-500/30', badge: 'bg-indigo-500/20 text-indigo-400', gradient: 'from-indigo-500/10 to-transparent' },
  pro: { icon: Crown, color: 'text-amber-400', border: 'border-amber-500/30', badge: 'bg-amber-500/20 text-amber-400', gradient: 'from-amber-500/10 to-transparent' },
};

const FEATURES = {
  free: ['3 generations/month', 'All preset access', 'Transcript downloads', 'Community presets'],
  starter: ['50 generations/month', 'All Free features', 'Best value credit packs', 'Priority processing', 'Batch uploads (10 files)'],
  pro: ['150 generations/month', 'All Starter features', 'Lowest credit prices', 'Bulk export (.docx)', 'Priority support'],
};

export default function PricingPage() {
  const { user, updateUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState(null);
  const [purchasing, setPurchasing] = useState(null);
  const [credits, setCredits] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [plansRes, creditsRes] = await Promise.all([
        planAPI.list(),
        planAPI.getCredits(),
      ]);
      setPlans(plansRes.data);
      setCredits(creditsRes.data);
    } catch {
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = async (planId) => {
    if (planId === credits?.plan) return;
    setSelecting(planId);
    try {
      const res = await planAPI.select(planId);
      toast.success(res.data.message);
      const meRes = await authAPI.me();
      updateUser(meRes.data);
      setCredits(prev => ({ ...prev, plan: planId, monthly_credits: res.data.monthly_credits, plan_name: plans.find(p => p.id === planId)?.name }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to switch plan');
    } finally {
      setSelecting(null);
    }
  };

  const handlePurchase = async (packIndex) => {
    setPurchasing(packIndex);
    try {
      const res = await planAPI.purchaseCredits(packIndex);
      toast.success(res.data.message);
      const creditsRes = await planAPI.getCredits();
      setCredits(creditsRes.data);
      const meRes = await authAPI.me();
      updateUser(meRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purchase failed');
    } finally {
      setPurchasing(null);
    }
  };

  const currentPlan = plans.find(p => p.id === credits?.plan) || plans[0];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-8 space-y-10 max-w-5xl mx-auto" data-testid="pricing-page">
      {/* Header */}
      <div className="text-center">
        <h1 className="font-heading text-3xl font-bold tracking-tight">Plans & Credits</h1>
        <p className="text-muted-foreground mt-2 max-w-lg mx-auto">
          Every generation uses 1 credit. Choose a plan that fits your workflow, and top up anytime.
        </p>

        {/* Current balance */}
        {credits && (
          <div className="inline-flex items-center gap-3 mt-4 px-5 py-2.5 rounded-full bg-secondary/40 border border-white/5" data-testid="credit-balance-display">
            <Zap className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium">{credits.total_credits} credits</span>
            <span className="text-xs text-muted-foreground">
              ({credits.monthly_credits} monthly + {credits.purchased_credits} purchased)
            </span>
          </div>
        )}
      </div>

      {/* Plan Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan) => {
          const meta = PLAN_META[plan.id] || PLAN_META.free;
          const Icon = meta.icon;
          const isCurrent = credits?.plan === plan.id;
          const features = FEATURES[plan.id] || [];

          return (
            <Card
              key={plan.id}
              className={cn(
                'relative overflow-hidden transition-all duration-300',
                isCurrent ? `${meta.border} border-2 bg-zinc-900/80` : 'border-white/5 bg-zinc-900/40 hover:border-white/10',
                plan.id === 'starter' && 'md:-translate-y-2'
              )}
              data-testid={`plan-card-${plan.id}`}
            >
              {plan.id === 'starter' && (
                <div className="absolute top-0 left-0 right-0 bg-indigo-500 text-white text-[10px] font-bold uppercase tracking-wider text-center py-1">
                  Most Popular
                </div>
              )}
              {meta.gradient && <div className={`absolute inset-0 bg-gradient-to-b ${meta.gradient} pointer-events-none`} />}
              <CardContent className={cn('relative p-6 space-y-5', plan.id === 'starter' && 'pt-9')}>
                <div className="flex items-center gap-2">
                  <Icon className={cn('w-5 h-5', meta.color)} />
                  <h3 className="font-heading text-lg font-bold">{plan.name}</h3>
                  {isCurrent && <Badge className={cn('text-[9px] ml-auto', meta.badge)}>Current</Badge>}
                </div>

                <div className="flex items-baseline gap-1">
                  <span className="font-heading text-4xl font-bold">${plan.price}</span>
                  <span className="text-sm text-muted-foreground">/mo</span>
                </div>

                <p className="text-sm text-muted-foreground">
                  <span className="font-semibold text-foreground">{plan.monthly_credits} credits</span>/month included
                </p>

                <ul className="space-y-2">
                  {features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check className={cn('w-4 h-4 mt-0.5 shrink-0', meta.color)} />
                      <span className="text-muted-foreground">{f}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleSelectPlan(plan.id)}
                  disabled={isCurrent || selecting !== null}
                  className={cn(
                    'w-full rounded-full transition-all duration-300',
                    isCurrent
                      ? 'bg-secondary text-muted-foreground cursor-default'
                      : plan.id === 'starter'
                        ? 'bg-indigo-500 hover:bg-indigo-600 shadow-lg shadow-indigo-500/25 hover:scale-[1.02]'
                        : plan.id === 'pro'
                          ? 'bg-amber-500 hover:bg-amber-600 text-black shadow-lg shadow-amber-500/25 hover:scale-[1.02]'
                          : 'bg-secondary hover:bg-secondary/80'
                  )}
                  data-testid={`select-plan-${plan.id}`}
                >
                  {selecting === plan.id
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : isCurrent
                      ? 'Current Plan'
                      : credits?.plan === 'pro' && plan.id !== 'pro'
                        ? 'Downgrade'
                        : plan.price === 0
                          ? 'Downgrade to Free'
                          : 'Upgrade'
                  }
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* What Credits Get You */}
      <Card className="bg-zinc-900/60 border-white/5" data-testid="credits-explainer">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Info className="w-5 h-5 text-primary" /> What Does 1 Credit Get You?
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="p-4 rounded-xl bg-secondary/20 border border-white/5 space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-primary" />
                </div>
                <span className="font-heading font-semibold text-sm">1 Generation</span>
              </div>
              <p className="text-xs text-muted-foreground">Each credit produces one complete social post package from any video clip using any preset.</p>
            </div>

            <div className="p-4 rounded-xl bg-secondary/20 border border-white/5">
              <p className="text-xs text-muted-foreground/80 font-medium uppercase tracking-wider mb-3">Each generation includes:</p>
              <ul className="space-y-2">
                {[
                  { icon: FileText, label: 'Optimized title for scroll-stopping hooks' },
                  { icon: MessageSquare, label: 'Platform-ready description (up to 280 chars)' },
                  { icon: Hash, label: '7 AI-optimized trending hashtags' },
                  { icon: Tag, label: '10 SEO keyword tags for discovery' },
                  { icon: ArrowRight, label: 'Custom CTA suggestion' },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                    <item.icon className="w-3.5 h-3.5 mt-0.5 text-primary shrink-0" />
                    {item.label}
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-4 rounded-xl bg-secondary/20 border border-white/5">
              <p className="text-xs text-muted-foreground/80 font-medium uppercase tracking-wider mb-3">How far do credits go?</p>
              <ul className="space-y-3">
                <li className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Casual creator</span>
                  <span className="font-medium">~3 clips/week = <span className="text-primary">12/mo</span></span>
                </li>
                <li className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Active creator</span>
                  <span className="font-medium">~1 clip/day = <span className="text-primary">30/mo</span></span>
                </li>
                <li className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Content team</span>
                  <span className="font-medium">~5 clips/day = <span className="text-primary">150/mo</span></span>
                </li>
                <li className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Agency</span>
                  <span className="font-medium">~10+ clips/day = <span className="text-primary">300+/mo</span></span>
                </li>
              </ul>
              <p className="text-[10px] text-muted-foreground/50 mt-3">Tip: Generate multiple options per clip to A/B test — each counts as 1 credit.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Credit Top-Up Packs */}
      <Card className="bg-zinc-900/60 border-white/5" data-testid="credit-packs">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-primary" /> Buy More Credits
            </CardTitle>
            <Badge variant="secondary" className="text-xs">
              {credits?.plan_name} pricing
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Purchased credits never expire. Higher plans unlock better per-credit rates.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {currentPlan?.credit_packs?.map((pack, i) => (
              <div
                key={i}
                className="p-5 rounded-xl bg-secondary/20 border border-white/5 hover:border-primary/20 transition-all duration-200 space-y-3"
                data-testid={`credit-pack-${i}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-heading text-2xl font-bold">{pack.credits}</span>
                  <Badge variant="secondary" className="text-[10px]">
                    ${pack.per_credit.toFixed(2)}/credit
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">credits</p>
                <Button
                  onClick={() => handlePurchase(i)}
                  disabled={purchasing !== null}
                  variant="secondary"
                  className="w-full rounded-full hover:scale-[1.02] transition-all duration-300"
                  data-testid={`buy-pack-${i}`}
                >
                  {purchasing === i
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : `$${pack.price}`
                  }
                </Button>
              </div>
            ))}
          </div>

          {credits?.plan !== 'pro' && (
            <p className="text-xs text-muted-foreground/60 text-center mt-4">
              Upgrade to {credits?.plan === 'free' ? 'Starter' : 'Pro'} for lower credit prices
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
